/**
 * PurchasedContactsTab — shared tab showing purchased/unlocked contacts.
 * Used in StudentDashboard, TutorDashboard, OrganizationDashboard.
 *
 * Props:
 *   purchasedContacts - array of purchase objects
 */
import React from 'react';
import { ShoppingBag, CheckCircle, XCircle, Mail, Phone } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const PurchasedContactsTab = ({ purchasedContacts = [] }) => (
    <Card>
        <CardHeader>
            <CardTitle className="flex items-center">
                <ShoppingBag className="h-5 w-5 mr-2" />
                Purchased Contacts
            </CardTitle>
            <CardDescription>
                View contacts you've unlocked. Contacts expire after 7 days.
            </CardDescription>
        </CardHeader>
        <CardContent>
            {purchasedContacts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                    <ShoppingBag className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>No purchased contacts yet. Unlock contacts to connect with tutors and organizations!</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {purchasedContacts.map((purchase) => (
                        <div
                            key={purchase.id}
                            className={`border rounded-lg p-4 ${purchase.is_expired ? 'bg-gray-50' : 'hover:shadow-md'} transition-shadow`}
                        >
                            <div className="flex justify-between items-start mb-3">
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <Badge variant={purchase.target_type === 'job' ? 'default' : 'secondary'}>
                                            {purchase.target_type.replace('_', ' ')}
                                        </Badge>
                                        <h3 className="font-semibold">{purchase.target_title}</h3>
                                    </div>
                                    <p className="text-sm text-gray-600">
                                        Purchased: {new Date(purchase.purchased_at).toLocaleDateString()}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        Price: {purchase.price_paid} coins
                                    </p>
                                </div>
                                <div className="text-right">
                                    {purchase.is_expired ? (
                                        <Badge variant="destructive" className="flex items-center gap-1">
                                            <XCircle className="h-3 w-3" />
                                            Expired
                                        </Badge>
                                    ) : (
                                        <Badge variant="success" className="flex items-center gap-1">
                                            <CheckCircle className="h-3 w-3" />
                                            {purchase.days_remaining} days left
                                        </Badge>
                                    )}
                                </div>
                            </div>

                            {!purchase.is_expired ? (
                                <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                                    <h4 className="font-semibold text-sm mb-2 text-green-800">Contact Details</h4>
                                    <div className="space-y-1 text-sm">
                                        {purchase.contact_details?.name && (
                                            <p><strong>Name:</strong> {purchase.contact_details.name}</p>
                                        )}
                                        {purchase.contact_details?.email && (
                                            <p className="flex items-center gap-2">
                                                <Mail className="h-4 w-4" />
                                                <a href={`mailto:${purchase.contact_details.email}`} className="text-blue-600 hover:underline">
                                                    {purchase.contact_details.email}
                                                </a>
                                            </p>
                                        )}
                                        {purchase.contact_details?.phone && (
                                            <p className="flex items-center gap-2">
                                                <Phone className="h-4 w-4" />
                                                <a href={`tel:${purchase.contact_details.phone}`} className="text-blue-600 hover:underline">
                                                    {purchase.contact_details.phone}
                                                </a>
                                            </p>
                                        )}
                                        {purchase.contact_details?.organization && (
                                            <p><strong>Organization:</strong> {purchase.contact_details.organization}</p>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                                    <p className="text-sm text-red-700 mb-2">
                                        <XCircle className="h-4 w-4 inline mr-1" />
                                        This contact has expired. Purchase again to regain access.
                                    </p>
                                    <Button size="sm" variant="outline" className="text-blue-600">
                                        Purchase Again
                                    </Button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </CardContent>
    </Card>
);

export default PurchasedContactsTab;
